import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, X, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import { EmptyState, PageBody, SectionCard, Callout } from '../../../shared/components/ui/primitives';
import {
  getApprovalCandidates,
  getApprovalDelegates,
  saveApprovalDelegates
} from '../../services/api/implementation/impl';
import { useApproverAccess, APPROVAL_TYPE_LABEL } from '../../hooks/useApproverAccess';

/**
 * The account owner picks who else may give final approval, per type.
 *
 * The owner can always approve. Naming people here adds them; it never takes
 * the owner out. Only the owner reaches this page — the server refuses the
 * calls for anybody else, and the page says so rather than showing an error.
 */
const Approvers = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=settings' });
  const access = useApproverAccess();

  const delegates = useQuery({
    queryKey: ['approvalDelegates'],
    queryFn: getApprovalDelegates,
    enabled: access.isOwner
  });

  const cards = delegates.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Approvers' }} />
      <PageBody>
        {!access.loaded && !access.failed ? (
          <Loader />
        ) : !access.isOwner ? (
          <EmptyState
            title="Only the account owner can choose approvers"
            hint="Ask the owner of this account if you need someone added."
          />
        ) : delegates.isLoading ? (
          <Loader />
        ) : (
          <>
            <Callout tone="info" collapsible icon={<Info size={14} />} title="How approvers work" className="sm:max-w-2xl">
              You can always approve everything yourself. For each type, only you and the people you add here can give the final approval.
            </Callout>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {cards.map((card) => (
                <TypeCard key={card.approval_type} card={card} />
              ))}
            </div>
          </>
        )}
      </PageBody>
    </Layout>
  );
};

const TypeCard = ({ card }) => {
  const queryClient = useQueryClient();
  const saved = useMemo(
    () =>
      (card.delegates ?? []).map((d) => ({
        id: d.user_id,
        name: d.name,
        vertical: d.vertical,
        designation: d.designation
      })),
    [card.delegates]
  );
  const [people, setPeople] = useState(saved);
  useEffect(() => setPeople(saved), [saved]);

  const dirty =
    people.length !== saved.length || people.some((p) => !saved.find((s) => s.id === p.id));

  const save = useMutation({
    mutationFn: () =>
      saveApprovalDelegates({
        approval_type: card.approval_type,
        user_ids: people.map((p) => p.id)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalDelegates'] });
      queryClient.invalidateQueries({ queryKey: ['approverAccess'] });
      toast.success('Approvers saved');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save approvers')
  });

  const label = card.label || APPROVAL_TYPE_LABEL[card.approval_type] || card.approval_type;

  return (
    <SectionCard title={label} subtitle={card.blurb}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {people.length === 0 ? (
            <span className="text-xs text-gray-500">Nobody yet — only you can approve.</span>
          ) : (
            people.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1.5 rounded-pill border border-hairline bg-gray-50 py-1 pl-3 pr-1.5 text-xs text-gray-800">
                <span className="font-medium">{p.name}</span>
                {p.designation || p.vertical ? (
                  <span className="text-gray-500">· {p.designation || p.vertical}</span>
                ) : null}
                <button
                  type="button"
                  aria-label={`Remove ${p.name}`}
                  onClick={() => setPeople((list) => list.filter((x) => x.id !== p.id))}
                  className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800">
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>

        <CandidatePicker
          exclude={people.map((p) => p.id)}
          onPick={(c) => setPeople((list) => [...list, c])}
        />

        <div className="flex items-center justify-end gap-2">
          {dirty && (
            <button
              type="button"
              onClick={() => setPeople(saved)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
              Undo
            </button>
          )}
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-lg bg-brand-700 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
};

const CandidatePicker = ({ exclude, onPick }) => {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);

  const candidates = useQuery({
    queryKey: ['approvalCandidates', q],
    queryFn: () => getApprovalCandidates(q),
    enabled: open,
    staleTime: 60 * 1000
  });

  const rows = (candidates.data ?? []).filter((c) => !exclude.includes(c.id));

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5">
        <Search size={14} className="text-gray-400" />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Add a person — search by name"
          autoComplete="off"
          className="w-full border-0 p-0 text-sm focus:outline-none focus:ring-0"
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {candidates.isFetching && rows.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">Nobody matches.</div>
          ) : (
            rows.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(c);
                  setText('');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50">
                <Plus size={14} className="text-brand-700" />
                <span className="font-medium text-gray-900">{c.name}</span>
                <span className="text-xs text-gray-500">
                  {[c.designation, c.vertical].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))
          )}
        </div>
      )}
      <p className="mt-1 text-xs text-gray-500">Changes take effect when you press Save.</p>
    </div>
  );
};

export default Approvers;
